/**
 * Search Results Session Storage Utility
 * 
 * Manages temporary search results that are passed between Search pages
 * and Sub-Project V2 pages via sessionStorage.
 */

import type { SearchResultsData, SearchResultDocument } from '../components/SearchResultsTab';

const STORAGE_KEY_PREFIX = 'sp_search_results_';
const COUNTER_KEY_PREFIX = 'sp_search_counter_';

export interface PendingSearchResults {
  documents: SearchResultDocument[];
  searchType: string;
  kbId: string;
  kbName?: string;
  query?: string;
  kwQuery?: string;
  smQuery?: string;
  searchMode?: string;
  topK?: number;
  topN?: number;
  enableReranking?: boolean;
  selectedFeatureIds?: string[];
  searchMetadata?: Record<string, unknown>;
  timestamp: string;
}

/**
 * Store search results for a sub-project
 * Returns the generated tab name (Docs1, Docs2, etc.)
 */
export function storeSearchResults(
  subProjectId: string,
  results: PendingSearchResults
): string {
  // Get or initialize the counter for this sub-project
  const counterKey = COUNTER_KEY_PREFIX + subProjectId;
  const currentCounter = parseInt(sessionStorage.getItem(counterKey) || '0', 10);
  const newCounter = currentCounter + 1;
  sessionStorage.setItem(counterKey, newCounter.toString());
  
  const tabName = `Docs${newCounter}`;
  const tabId = `search-results-${subProjectId}-${newCounter}`;
  
  const searchResultsData: SearchResultsData = {
    tabId,
    tabName,
    documents: results.documents,
    searchType: results.searchType,
    kbId: results.kbId,
    kbName: results.kbName,
    query: results.query,
    kwQuery: results.kwQuery,
    smQuery: results.smQuery,
    searchMode: results.searchMode,
    topK: results.topK,
    topN: results.topN,
    enableReranking: results.enableReranking,
    selectedFeatureIds: results.selectedFeatureIds,
    searchMetadata: results.searchMetadata,
    timestamp: results.timestamp,
    isSaved: false,
    isSearchSaved: false,
  };
  
  // Get existing results array or create new one
  const storageKey = STORAGE_KEY_PREFIX + subProjectId;
  const existingResultsJson = sessionStorage.getItem(storageKey);
  const existingResults: SearchResultsData[] = existingResultsJson 
    ? JSON.parse(existingResultsJson) 
    : [];
  
  existingResults.push(searchResultsData);
  sessionStorage.setItem(storageKey, JSON.stringify(existingResults));
  
  return tabId;
}

/**
 * Get all pending search results for a sub-project
 */
export function getPendingSearchResults(subProjectId: string): SearchResultsData[] {
  const storageKey = STORAGE_KEY_PREFIX + subProjectId;
  const resultsJson = sessionStorage.getItem(storageKey);
  
  if (!resultsJson) {
    return [];
  }
  
  try {
    return JSON.parse(resultsJson);
  } catch (e) {
    console.error('Failed to parse search results from sessionStorage:', e);
    return [];
  }
}

/**
 * Mark a search session as saved
 */
export function markSearchSaved(subProjectId: string, tabId: string): void {
  const storageKey = STORAGE_KEY_PREFIX + subProjectId;
  const results = getPendingSearchResults(subProjectId);
  
  const updatedResults = results.map(r => 
    r.tabId === tabId ? { ...r, isSearchSaved: true } : r
  );
  
  sessionStorage.setItem(storageKey, JSON.stringify(updatedResults));
}

/**
 * Mark a search results tab as saved
 */
export function markSearchResultsSaved(subProjectId: string, tabId: string): void {
  const storageKey = STORAGE_KEY_PREFIX + subProjectId;
  const results = getPendingSearchResults(subProjectId);
  
  const updatedResults = results.map(r => 
    r.tabId === tabId ? { ...r, isSaved: true } : r
  );
  
  sessionStorage.setItem(storageKey, JSON.stringify(updatedResults));
}

/**
 * Remove a search results tab
 */
export function removeSearchResults(subProjectId: string, tabId: string): void {
  const storageKey = STORAGE_KEY_PREFIX + subProjectId;
  const results = getPendingSearchResults(subProjectId);
  
  const updatedResults = results.filter(r => r.tabId !== tabId);
  
  if (updatedResults.length === 0) {
    sessionStorage.removeItem(storageKey);
  } else {
    sessionStorage.setItem(storageKey, JSON.stringify(updatedResults));
  }
}

/**
 * Check if there are any unsaved search results for a sub-project
 */
export function hasUnsavedSearchResults(subProjectId: string): boolean {
  const results = getPendingSearchResults(subProjectId);
  return results.some(r => !r.isSaved);
}

/**
 * Clear all search results for a sub-project
 */
export function clearAllSearchResults(subProjectId: string): void {
  const storageKey = STORAGE_KEY_PREFIX + subProjectId;
  const counterKey = COUNTER_KEY_PREFIX + subProjectId;
  sessionStorage.removeItem(storageKey);
  sessionStorage.removeItem(counterKey);
}
